import api from './axios';
import { ApiResponse, Event } from '../types';

// Transform backend response (snake_case) to frontend format (camelCase)
const transformEvent = (backendEvent: any): Event => {
  return {
    id: backendEvent._id || backendEvent.id,
    name: backendEvent.name,
    description: backendEvent.description,
    startDate: backendEvent.start_date || backendEvent.startDate,
    endDate: backendEvent.end_date || backendEvent.endDate,
    location: backendEvent.location,
    bannerUrl: backendEvent.banner_url || backendEvent.bannerUrl,
    status: backendEvent.status,
    registrationOpen: backendEvent.registration_open !== undefined ? backendEvent.registration_open : backendEvent.registrationOpen,
    isActive: backendEvent.is_active !== undefined ? backendEvent.is_active : backendEvent.isActive,
    createdAt: backendEvent.createdAt || backendEvent.created_at,
    updatedAt: backendEvent.updatedAt || backendEvent.updated_at,
    createdBy: backendEvent.created_by || backendEvent.createdBy,
    updatedBy: backendEvent.updated_by || backendEvent.updatedBy,
  };
};

const transformEvents = (events: any[]): Event[] => {
  return events.map(transformEvent);
};

// Types for API responses
export type EventFilters = {
  page?: number;
  limit?: number;
  status?: string; // "current", "upcoming", "past", "registration_open"
  registrationOpen?: boolean;
  search?: string;
};

export type CreateEventPayload = {
  name: string;
  description?: string;
  startDate: string;
  endDate?: string;
  location?: string;
  bannerUrl?: string;
  status?: string;
  registrationOpen?: boolean;
};

export type UpdateEventPayload = Partial<CreateEventPayload>;

export type AdminEventFilters = EventFilters & {
  isActive?: boolean;
};

// Public endpoints (authenticated users)

export const getEvents = async (
  filters: EventFilters = {}
): Promise<ApiResponse<{ events: Event[]; total: number; page: number; totalPages: number }>> => {
  const params: any = {
    page: filters.page || 1,
    limit: filters.limit || 10,
  };
  
  if (filters.status) {
    params.status = filters.status;
  }
  if (filters.registrationOpen !== undefined) {
    params.registrationOpen = filters.registrationOpen;
  }
  if (filters.search) {
    params.search = filters.search;
  }

  const response = await api.get<any>('/api/v1/events', { params });
  
  const responseData = response.data;
  let eventsData: any[] = [];
  let total = 0;
  let page = filters.page || 1;
  let totalPages = 1;

  if (responseData && responseData.data) {
    if (responseData.data.events && Array.isArray(responseData.data.events)) {
      eventsData = responseData.data.events;
      total = responseData.data.total || eventsData.length;
      page = responseData.data.page || page;
      totalPages = responseData.data.totalPages || responseData.data.total_pages || Math.ceil(total / (filters.limit || 10));
    } else if (Array.isArray(responseData.data)) {
      eventsData = responseData.data;
      total = eventsData.length;
      totalPages = Math.ceil(total / (filters.limit || 10));
    }
  } else if (Array.isArray(responseData)) {
    eventsData = responseData;
    total = eventsData.length;
    totalPages = Math.ceil(total / (filters.limit || 10));
  }
  
  return {
    success: responseData?.success !== false,
    message: responseData?.message,
    data: {
      events: transformEvents(eventsData),
      total,
      page,
      totalPages,
    },
  };
};

export const getCurrentEvents = async (
  limit: number = 10
): Promise<ApiResponse<Event[]>> => {
  const response = await api.get<any>('/api/v1/events/current', {
    params: { limit },
  });
  
  const responseData = response.data;
  let eventsData: any[] = [];
  
  if (responseData && responseData.data) {
    if (Array.isArray(responseData.data)) {
      eventsData = responseData.data;
    } else if (responseData.data.events && Array.isArray(responseData.data.events)) {
      eventsData = responseData.data.events;
    }
  } else if (Array.isArray(responseData)) {
    eventsData = responseData;
  }
  
  return {
    success: responseData?.success !== false,
    message: responseData?.message,
    data: transformEvents(eventsData),
  };
};

export const getUpcomingEvents = async (
  limit: number = 10
): Promise<ApiResponse<Event[]>> => {
  const response = await api.get<any>('/api/v1/events/upcoming', {
    params: { limit },
  });
  
  const responseData = response.data;
  let eventsData: any[] = [];
  
  if (responseData && responseData.data) {
    if (Array.isArray(responseData.data)) {
      eventsData = responseData.data;
    } else if (responseData.data.events && Array.isArray(responseData.data.events)) {
      eventsData = responseData.data.events;
    }
  } else if (Array.isArray(responseData)) {
    eventsData = responseData;
  }
  
  return {
    success: responseData?.success !== false,
    message: responseData?.message,
    data: transformEvents(eventsData),
  };
};

export const getHeroEvent = async (): Promise<ApiResponse<Event | null>> => {
  try {
    const response = await api.get<any>('/api/v1/events/hero');
    
    const responseData = response.data;
    if (responseData && responseData.data) {
      return {
        success: responseData.success !== false,
        message: responseData.message,
        data: transformEvent(responseData.data),
      };
    }
    
    return {
      success: false,
      message: 'No hero event found',
      data: null,
    };
  } catch (error: any) {
    if (error.response?.status === 404) {
      return {
        success: false,
        message: 'No hero event found',
        data: null,
      };
    }
    throw error;
  }
};

export const getEventById = async (eventId: string): Promise<ApiResponse<Event>> => {
  const response = await api.get<any>(`/api/v1/events/${eventId}`);
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message,
    data: transformEvent(response.data.data || response.data),
  };
};

// Admin endpoints

export const getAdminEvents = async (
  filters: AdminEventFilters = {}
): Promise<ApiResponse<{ events: Event[]; total: number; page: number; totalPages: number }>> => {
  const params: any = {
    page: filters.page || 1,
    limit: filters.limit || 10,
  };
  
  if (filters.status) {
    params.status = filters.status;
  }
  if (filters.registrationOpen !== undefined) {
    params.registrationOpen = filters.registrationOpen;
  }
  if (filters.isActive !== undefined) {
    params.isActive = filters.isActive;
  }
  if (filters.search) {
    params.search = filters.search;
  }

  const response = await api.get<any>('/api/v1/admin/events', { params });
  
  const responseData = response.data;
  let eventsData: any[] = [];
  let total = 0;
  let page = filters.page || 1;
  let totalPages = 1;

  if (responseData && responseData.data) {
    if (responseData.data.events && Array.isArray(responseData.data.events)) {
      eventsData = responseData.data.events;
      total = responseData.data.total || eventsData.length;
      page = responseData.data.page || page;
      totalPages = responseData.data.totalPages || responseData.data.total_pages || Math.ceil(total / (filters.limit || 10));
    } else if (Array.isArray(responseData.data)) {
      eventsData = responseData.data;
      total = eventsData.length;
      totalPages = Math.ceil(total / (filters.limit || 10));
    }
  } else if (Array.isArray(responseData)) {
    eventsData = responseData;
    total = eventsData.length;
    totalPages = Math.ceil(total / (filters.limit || 10));
  }
  
  return {
    success: responseData?.success !== false,
    message: responseData?.message,
    data: {
      events: transformEvents(eventsData),
      total,
      page,
      totalPages,
    },
  };
};

export const getAdminEvent = async (eventId: string): Promise<ApiResponse<Event>> => {
  const response = await api.get<any>(`/api/v1/admin/events/${eventId}`);
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message,
    data: transformEvent(response.data.data || response.data),
  };
};

export const createEvent = async (payload: CreateEventPayload): Promise<ApiResponse<Event>> => {
  // Transform camelCase to snake_case for backend
  const backendPayload: any = {
    name: payload.name,
    description: payload.description,
    start_date: payload.startDate,
    end_date: payload.endDate,
    location: payload.location,
    banner_url: payload.bannerUrl,
    status: payload.status,
    registration_open: payload.registrationOpen,
  };
  
  console.log('createEvent - Frontend payload:', payload);
  console.log('createEvent - Backend payload before cleanup:', backendPayload);
  
  // Remove undefined fields
  Object.keys(backendPayload).forEach(key => {
    if (backendPayload[key] === undefined) {
      delete backendPayload[key];
    }
  });

  console.log('createEvent - Backend payload after cleanup:', backendPayload);
  console.log('createEvent - start_date value:', backendPayload.start_date);
  console.log('createEvent - start_date type:', typeof backendPayload.start_date);

  const response = await api.post<any>('/api/v1/events', backendPayload);
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message || 'Event created successfully',
    data: transformEvent(response.data.data || response.data),
  };
};

export const createEventWithImage = async (formData: FormData): Promise<ApiResponse<Event>> => {
  const response = await api.post<any>('/api/v1/events', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message || 'Event created successfully',
    data: transformEvent(response.data.data || response.data),
  };
};

export const updateEvent = async (
  eventId: string,
  payload: UpdateEventPayload
): Promise<ApiResponse<Event>> => {
  // Transform camelCase to snake_case for backend
  const backendPayload: any = {};
  
  if (payload.name !== undefined) backendPayload.name = payload.name;
  if (payload.description !== undefined) backendPayload.description = payload.description;
  if (payload.startDate !== undefined) backendPayload.start_date = payload.startDate;
  if (payload.endDate !== undefined) backendPayload.end_date = payload.endDate;
  if (payload.location !== undefined) backendPayload.location = payload.location;
  if (payload.bannerUrl !== undefined) backendPayload.banner_url = payload.bannerUrl;
  if (payload.status !== undefined) backendPayload.status = payload.status;
  if (payload.registrationOpen !== undefined) backendPayload.registration_open = payload.registrationOpen;

  const response = await api.put<any>(`/api/v1/events/${eventId}`, backendPayload);
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message || 'Event updated successfully',
    data: transformEvent(response.data.data || response.data),
  };
};

export const deleteEvent = async (eventId: string): Promise<ApiResponse<void>> => {
  const response = await api.delete<any>(`/api/v1/events/${eventId}`);
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message || 'Event deleted successfully',
    data: undefined,
  };
};

export const toggleEventRegistration = async (
  eventId: string,
  registrationOpen: boolean
): Promise<ApiResponse<Event>> => {
  const response = await api.patch<any>(`/api/v1/admin/events/${eventId}/registration`, {
    registration_open: registrationOpen,
  });
  
  return {
    success: response.data?.success !== false,
    message: response.data?.message || 'Registration status updated',
    data: transformEvent(response.data.data || response.data),
  };
};
